import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Package, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicUnitPeek() {
  const { unitId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUnitData();
  }, [unitId]);

  const fetchUnitData = async () => {
    try {
      const response = await axios.get(`${API}/public/unit/${unitId}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching unit:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFFFF' }}>
        <div className="text-xl" style={{ color: '#0B0B0B' }}>Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFFFF' }}>
        <div className="text-xl" style={{ color: '#FF6B6B' }}>Unit not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FFFFFF', padding: '40px 20px' }}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#47FFB0', fontFamily: 'Space Grotesk' }}>
            {data.unit.portfolio_name}
          </h1>
          <p className="text-2xl" style={{ color: '#4B9DFF' }}>
            Unit {data.unit.serial}
          </p>
        </div>

        {/* Unit Image */}
        {data.unit.image_url && (
          <div className="mb-8">
            <img 
              src={data.unit.image_url} 
              alt={`Unit ${data.unit.serial}`}
              className="w-full max-w-lg mx-auto rounded-lg shadow-2xl"
              style={{ maxHeight: '500px', objectFit: 'cover' }}
            />
          </div>
        )}

        {/* Unit Info */}
        <Card className="glass-card border-primary/20 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#4B9DFF' }}>
              <Package className="w-5 h-5" />
              Unit Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span style={{ color: '#999' }}>Serial Number:</span>
                <span style={{ color: '#47FFB0', fontWeight: '600' }}>{data.unit.serial}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#999' }}>Portfolio:</span>
                <span style={{ color: '#0B0B0B' }}>{data.unit.portfolio_name}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* View Portfolio Link */}
        <Link 
          to={data.portfolio_link}
          className="block w-full"
        >
          <button
            className="w-full p-4 rounded-lg flex items-center justify-center gap-2"
            style={{ 
              background: 'linear-gradient(135deg, #4B9DFF, #47FFB0)',
              color: '#0D0D0D',
              fontWeight: '600',
              fontSize: '16px'
            }}
          >
            <ExternalLink className="w-5 h-5" />
            View Full Portfolio Snapshot
          </button>
        </Link>

        <p className="text-center text-sm mt-4" style={{ color: '#999' }}>
          Scan the Portfolio QR to see complete details, utilities, and payout rules
        </p>
      </div>
    </div>
  );
}
